import { useTranslation } from 'react-i18next'
import { useDroppable } from '@dnd-kit/core'
import { Plus } from 'lucide-react'
import type { AddToProductTarget, DropTargetData } from '../types'
import type { SlotFill } from './AddToProductSlotGrid'
import type { MediaSize } from './MediaSizeToggle'
import { IMAGE_SIZES } from './mediaConstants'
import { SlotDeleteButton } from './slot/SlotDeleteButton'

interface KiReferenceSlotsProps {
  filled: Record<number, SlotFill>
  size?: MediaSize
  onDelete?: (target: AddToProductTarget) => void
}

interface AiRefSlotContentProps {
  filled: SlotFill
  n: number
  onDelete?: (target: AddToProductTarget) => void
}

function AiRefSlotContent({ filled, n, onDelete }: AiRefSlotContentProps) {
  const { t } = useTranslation()
  return (
    <>
      <img src={filled.url} alt="" className="w-full h-full object-cover" />
      {filled.isPending && (
        <span className="absolute bottom-1 right-1 px-1.5 py-0.5 text-3xs font-semibold uppercase tracking-wide rounded bg-amber-500 text-black">
          {t('addToProduct.media.unsavedBadge')}
        </span>
      )}
      {onDelete && (
        <SlotDeleteButton
          target={{ type: 'ai_reference', slot: n }}
          onDelete={onDelete}
        />
      )}
    </>
  )
}

interface AiRefSlotProps {
  n: number
  filled?: SlotFill
  onDelete?: (target: AddToProductTarget) => void
}

function aiRefRingClass(isPending: boolean, isOver: boolean): string {
  if (isPending) return 'border-amber-500 ring-2 ring-amber-300/40'
  if (isOver) return 'border-orange-500 bg-orange-50 dark:bg-orange-950/30'
  return 'border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900'
}

function AiRefSlot({ n, filled, onDelete }: AiRefSlotProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: `airef-slot-${n}`,
    data: { target: 'ai_reference', slot: n } satisfies DropTargetData,
  })
  const ring = aiRefRingClass(Boolean(filled?.isPending), isOver)
  return (
    <div
      ref={setNodeRef}
      data-testid={`atp-airef-${n}`}
      data-pending={filled?.isPending ? 'true' : undefined}
      className={`group aspect-square rounded-xl overflow-hidden relative border-2 border-dashed flex items-center justify-center ${ring}`}
    >
      {filled ? (
        <AiRefSlotContent filled={filled} n={n} onDelete={onDelete} />
      ) : (
        <Plus className="w-5 h-5 text-neutral-400" />
      )}
    </div>
  )
}

/**
 * KI reference slots block: 4 droppable slots carrying
 * `{ target:'ai_reference', slot }`. `size` mirrors the channel slot grid so
 * both grids visually align.
 */
export function KiReferenceSlots({
  filled,
  size = 'M',
  onDelete,
}: KiReferenceSlotsProps) {
  const { t } = useTranslation()
  const minPx = IMAGE_SIZES[size]
  return (
    <section>
      <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
        {t('addToProduct.aiReference.title')}
      </h3>
      <div
        data-size={size}
        className="grid gap-3"
        style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${minPx}px, 1fr))` }}
      >
        {[1, 2, 3, 4].map((n) => (
          <AiRefSlot key={n} n={n} filled={filled[n]} onDelete={onDelete} />
        ))}
      </div>
    </section>
  )
}
